// Design Tokens for MystiCrafter
// A stunning, magical alchemy game design system

export const colors = {
  // Brand Colors - Magical Alchemy Theme
  brand: {
    primary: '#65A30D', // Lemon-lime (brand)
    secondary: '#EC4899', // Hot pink
    accent: '#F59E0B', // Amber/gold
    success: '#10B981', // Emerald
    error: '#EF4444', // Red
    warning: '#F59E0B', // Amber
    info: '#3B82F6', // Blue
  },

  // Magical Gradients
  gradients: {
    primary: 'linear-gradient(135deg, #84CC16 0%, #4D7C0F 100%)',
    secondary: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
    gold: 'linear-gradient(135deg, #f6d365 0%, #fda085 100%)',
    fire: 'linear-gradient(135deg, #ff6b6b 0%, #ee5a24 100%)',
    water: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
    earth: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
    air: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
    mystical: 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)',
    dark: 'linear-gradient(135deg, #2d3748 0%, #1a202c 100%)',
    success: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
    legendary: 'linear-gradient(135deg, #FFD700 0%, #FF8C00 50%, #FF1493 100%)',
  },

  // Element Tier Colors
  elementTiers: {
    1: { color: '#9CA3AF', glow: '#E5E7EB' }, // Gray - Basic
    2: { color: '#60A5FA', glow: '#DBEAFE' }, // Blue - Common
    3: { color: '#34D399', glow: '#D1FAE5' }, // Green - Uncommon
    4: { color: '#A78BFA', glow: '#EDE9FE' }, // Purple - Rare
    5: { color: '#F472B6', glow: '#FCE7F3' }, // Pink - Epic
    6: { color: '#FB923C', glow: '#FED7AA' }, // Orange - Legendary
    7: { color: '#FBBF24', glow: '#FEF3C7' }, // Gold - Mythic
  },

  // Background Colors
  background: {
    primary: '#0F172A', // Dark navy
    secondary: '#1E293B', // Slate
    tertiary: '#334155', // Light slate
    card: '#1E293B',
    cardHover: '#334155',
    modal: 'rgba(15, 23, 42, 0.95)',
  },

  // Text Colors
  text: {
    primary: '#F1F5F9',
    secondary: '#CBD5E1',
    tertiary: '#94A3B8',
    disabled: '#64748B',
  },
};

export const spacing = {
  xs: '0.25rem', // 4px
  sm: '0.5rem', // 8px
  md: '1rem', // 16px
  lg: '1.5rem', // 24px
  xl: '2rem', // 32px
  '2xl': '3rem', // 48px
  '3xl': '4rem', // 64px
};

export const borderRadius = {
  sm: '0.375rem', // 6px
  md: '0.5rem', // 8px
  lg: '0.75rem', // 12px
  xl: '1rem', // 16px
  '2xl': '1.5rem', // 24px
  full: '9999px',
};

export const shadows = {
  sm: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
  md: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
  lg: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
  xl: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
  '2xl': '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
  glow: '0 0 20px rgba(101, 163, 13, 0.5)',
  glowStrong: '0 0 30px rgba(101, 163, 13, 0.8)',
};

export const animations = {
  // Duration
  duration: {
    fast: '150ms',
    normal: '300ms',
    slow: '500ms',
    slower: '800ms',
  },

  // Easing
  easing: {
    easeInOut: 'cubic-bezier(0.4, 0, 0.2, 1)',
    easeOut: 'cubic-bezier(0.0, 0, 0.2, 1)',
    easeIn: 'cubic-bezier(0.4, 0, 1, 1)',
    bounce: 'cubic-bezier(0.68, -0.55, 0.265, 1.55)',
  },

  // Keyframes
  keyframes: {
    pulse: `
      @keyframes pulse {
        0%, 100% { opacity: 1; }
        50% { opacity: 0.5; }
      }
    `,
    bounce: `
      @keyframes bounce {
        0%, 100% {
          transform: translateY(-25%);
          animation-timing-function: cubic-bezier(0.8, 0, 1, 1);
        }
        50% {
          transform: translateY(0);
          animation-timing-function: cubic-bezier(0, 0, 0.2, 1);
        }
      }
    `,
    spin: `
      @keyframes spin {
        from { transform: rotate(0deg); }
        to { transform: rotate(360deg); }
      }
    `,
    fadeIn: `
      @keyframes fadeIn {
        from { opacity: 0; transform: translateY(10px); }
        to { opacity: 1; transform: translateY(0); }
      }
    `,
    scaleIn: `
      @keyframes scaleIn {
        from { opacity: 0; transform: scale(0.9); }
        to { opacity: 1; transform: scale(1); }
      }
    `,
    shimmer: `
      @keyframes shimmer {
        0% { background-position: -1000px 0; }
        100% { background-position: 1000px 0; }
      }
    `,
    float: `
      @keyframes float {
        0%, 100% { transform: translateY(0px); }
        50% { transform: translateY(-10px); }
      }
    `,
    glow: `
      @keyframes glow {
        0%, 100% { box-shadow: 0 0 5px rgba(101, 163, 13, 0.5); }
        50% { box-shadow: 0 0 20px rgba(101, 163, 13, 0.8); }
      }
    `,
  },
};

export const typography = {
  fontFamily: {
    sans: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    display: '"Inter", -apple-system, BlinkMacSystemFont, sans-serif',
    mono: '"Fira Code", "Consolas", monospace',
  },

  fontSize: {
    xs: '0.75rem', // 12px
    sm: '0.875rem', // 14px
    base: '1rem', // 16px
    lg: '1.125rem', // 18px
    xl: '1.25rem', // 20px
    '2xl': '1.5rem', // 24px
    '3xl': '1.875rem', // 30px
    '4xl': '2.25rem', // 36px
    '5xl': '3rem', // 48px
  },

  fontWeight: {
    normal: '400',
    medium: '500',
    semibold: '600',
    bold: '700',
    extrabold: '800',
  },
};

export const zIndex = {
  base: 0,
  dropdown: 10,
  sticky: 20,
  modal: 30,
  popover: 40,
  tooltip: 50,
  notification: 60,
};

// Particle Effect Configurations
export const particles = {
  discovery: {
    count: 50,
    colors: ['#65A30D', '#EC4899', '#F59E0B', '#10B981'],
    size: { min: 2, max: 6 },
    speed: { min: 1, max: 3 },
    lifetime: 2000,
  },
  combine: {
    count: 30,
    colors: ['#60A5FA', '#34D399', '#FBBF24'],
    size: { min: 3, max: 8 },
    speed: { min: 2, max: 4 },
    lifetime: 1500,
  },
  celebration: {
    count: 100,
    colors: ['#FFD700', '#FF8C00', '#FF1493', '#65A30D'],
    size: { min: 4, max: 10 },
    speed: { min: 3, max: 6 },
    lifetime: 3000,
  },
};
